/*
 * Multi-line text, with an optional character counter.
 *
 * The counter is the interesting part. The visible "120 / 500" is
 * `aria-hidden`, because a live region that updates on every keystroke turns a
 * screen reader into a metronome and makes the field unusable: the person hears
 * the count instead of the letters they are typing.
 *
 * What is announced instead is a message that depends on the band the count is
 * in rather than on the count itself: nothing until the limit is close, one
 * message while it is close, and one more when it is reached. Because the text
 * of each message is constant within its band, the live region's contents only
 * change at the two crossings, which is the only place the number matters. An
 * earlier version announced the exact figure and had to suppress the repeats
 * with an effect and a ref; making the message coarser removed the state
 * entirely and says the more useful thing anyway.
 *
 * The counter is `aria-hidden` and still referenced by `aria-describedby`, which
 * is not a contradiction: aria-hidden keeps a stray "120 / 500" out of the
 * reading order when somebody browses the page, while the accessible
 * description computation follows the reference regardless. So the exact count
 * is read on focus, once, which is where it belongs.
 *
 * `maxLength` is enforced by the browser, so this is a warning rather than a
 * validation. The alternative, letting the field overrun and rejecting it on
 * submit, means somebody writes a broadcast, presses send, and is told
 * afterwards that the last two sentences have to go.
 */

import { useId, useState } from 'react'
import type { ChangeEvent, TextareaHTMLAttributes } from 'react'
import { joinIds } from '../aria'
import { useFieldControl } from '../Field/fieldContext'
import styles from './Textarea.module.css'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Shows the counter. Has no effect without `maxLength`, which it counts against. */
  showCount?: boolean
  /** How many characters may be left before the counter starts warning. */
  warnAt?: number
  /**
   * The seam for localisation. The console has no message catalogue yet, and
   * hard-coding English inside a live region is the kind of string that is
   * still there two years later because nothing ever fails when it is wrong.
   */
  formatNearLimit?: (within: number) => string
  formatAtLimit?: (limit: number) => string
}

const defaultFormatNearLimit = (within: number) => `${within} characters or fewer remaining`

const defaultFormatAtLimit = (limit: number) => `Character limit of ${limit} reached`

export function Textarea({
  showCount = false,
  warnAt = 20,
  formatNearLimit = defaultFormatNearLimit,
  formatAtLimit = defaultFormatAtLimit,
  className,
  onChange,
  rows = 4,
  ...rest
}: TextareaProps) {
  const field = useFieldControl()
  const countId = useId()

  /*
   * Length is tracked here rather than read from `value`, so the counter works
   * for an uncontrolled textarea too. When the component is controlled the prop
   * wins, because a parent that clamps or rewrites the value would otherwise
   * leave the counter reporting what was typed rather than what was kept.
   */
  const [typedLength, setTypedLength] = useState(() => String(rest.defaultValue ?? '').length)
  const length = typeof rest.value === 'string' ? rest.value.length : typedLength

  const { maxLength } = rest
  const remaining = maxLength === undefined ? undefined : Math.max(maxLength - length, 0)
  const counted = showCount && maxLength !== undefined && remaining !== undefined

  const announcement =
    !counted || maxLength === undefined || remaining === undefined
      ? ''
      : remaining === 0
        ? formatAtLimit(maxLength)
        : remaining <= warnAt
          ? formatNearLimit(warnAt)
          : ''

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setTypedLength(event.target.value.length)
    onChange?.(event)
  }

  const textareaClassName = [styles.textarea, className].filter(Boolean).join(' ')

  return (
    <div className={styles.wrap}>
      <textarea
        {...rest}
        rows={rows}
        id={rest.id ?? field?.id}
        className={textareaClassName}
        onChange={handleChange}
        aria-describedby={joinIds(rest['aria-describedby'], field?.describedBy, counted && countId)}
        aria-invalid={rest['aria-invalid'] ?? (field?.invalid ? true : undefined)}
        aria-required={rest['aria-required'] ?? (field?.required ? true : undefined)}
      />
      {counted ? (
        <>
          <span
            className={[styles.count, announcement === '' ? '' : styles.countWarning]
              .filter(Boolean)
              .join(' ')}
            id={countId}
            aria-hidden="true"
          >
            {length} / {maxLength}
          </span>
          <span className="visually-hidden" role="status">
            {announcement}
          </span>
        </>
      ) : null}
    </div>
  )
}
